/**
 * Преобразует дату из формата YYYY-MM-DD в DD-MM-YYYY HH:mm:ssZ
 * @param dateString Дата в формате YYYY-MM-DD
 * @returns Отформатированная дата в формате DD-MM-YYYY HH:mm:ssZ или исходная строка
 */
export const formatDateForApi = (dateString?: string): string | undefined => {
  if (!dateString) return undefined;

  // Проверяем формат входной даты
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const [year, month, day] = dateString.split('-');
    // Возвращаем дату в формате DD-MM-YYYY 00:00:00Z
    return `${day}-${month}-${year} 00:00:00Z`;
  }

  return dateString; // Если формат не соответствует ожидаемому, возвращаем как есть
};

const moscowDateFormat = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Moscow',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * Дата по московскому времени в формате YYYY-MM-DD (независимо от часового пояса браузера)
 */
export const toMoscowDate = (date: Date): string => moscowDateFormat.format(date);

export type DatePeriod = 'week' | 'month' | 'year';

/**
 * Диапазон дат по Москве для фильтра периода: { dateFrom, dateTo } в формате YYYY-MM-DD, включительно.
 */
export const getMoscowPeriodRange = (period: DatePeriod, now: Date = new Date()) => {
  const dateTo = toMoscowDate(now);
  const [year, month] = dateTo.split('-');

  switch (period) {
    case 'week':
      return { dateFrom: toMoscowDate(new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)), dateTo };
    case 'month':
      return { dateFrom: `${year}-${month}-01`, dateTo };
    case 'year':
      return { dateFrom: `${year}-01-01`, dateTo };
  }
};

// Даты из API. epl_backend (internal/types/converter.go) шлёт их днём вперёд, «DD-MM-YYYY[ HH:mm:ss[.US]]»,
// уже по Москве, поэтому части берём как есть, без Date и часовых поясов. Даты записей бывают
// «YYYY-MM-DD», а профиль водителя из json_build_object — момент ISO; его переводим на Москву.
// Тот же разбор, что parseBackendDate в epl_web (ddf0bac).
export interface IBackendDate {
  day: string;
  month: string;
  year: string;
  // «HH:mm»; нет — дата без времени
  time?: string;
}

// Что показать вместо отсутствующей даты в таблицах и карточках
export const NO_DATE = '-';

const DAY_FIRST = /^(\d{2})-(\d{2})-(\d{4})(?:[ T](\d{2}):(\d{2}))?/;
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

const moscowPartsFormat = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Moscow',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

function moscowParts(value: string): IBackendDate | null {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const parts = moscowPartsFormat.formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? '';
  return { day: part('day'), month: part('month'), year: part('year'), time: `${part('hour')}:${part('minute')}` };
}

function matchBackendDate(value: string): IBackendDate | null {
  const dayFirst = DAY_FIRST.exec(value);
  if (dayFirst) {
    const [, day, month, year, hour, minute] = dayFirst;
    return { day, month, year, ...(hour ? { time: `${hour}:${minute}` } : {}) };
  }
  const isoDate = ISO_DATE.exec(value);
  if (isoDate) {
    const [, year, month, day] = isoDate;
    return { day, month, year };
  }
  return moscowParts(value);
}

// 31-02 — не дата; «01-01-0001» — нулевое время Go, а не день регистрации
function isRealDate({ day, month, year }: IBackendDate): boolean {
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  return Number(year) > 1 && date.getUTCMonth() === Number(month) - 1 && date.getUTCDate() === Number(day);
}

export function parseBackendDate(value: string | null | undefined): IBackendDate | null {
  const parts = value ? matchBackendDate(value) : null;
  return parts && isRealDate(parts) ? parts : null;
}

// Форматы показа. fallback — что показать без даты: у каждого экрана своё («-», «Нет данных», пусто)

// «DD.MM.YYYY»
export function formatBackendDate(value: string | null | undefined, fallback = ''): string {
  const date = parseBackendDate(value);
  return date ? `${date.day}.${date.month}.${date.year}` : fallback;
}

// «DD.MM.YY» — таблицы осмотров и путевых листов
export function formatBackendShortDate(value: string | null | undefined, fallback = ''): string {
  const date = parseBackendDate(value);
  return date ? `${date.day}.${date.month}.${date.year.slice(-2)}` : fallback;
}

// «HH:mm»
export function formatBackendTime(value: string | null | undefined, fallback = ''): string {
  return parseBackendDate(value)?.time ?? fallback;
}

// «DD.MM.YYYY, HH:mm»; без времени — только дата
export function formatBackendDateTime(value: string | null | undefined, fallback = ''): string {
  const date = parseBackendDate(value);
  if (!date) return fallback;
  const day = `${date.day}.${date.month}.${date.year}`;
  return date.time ? `${day}, ${date.time}` : day;
}

// «YYYY-MM-DD» для <input type="date">; нет даты — пустое поле
export function formatDateForInput(value: string | null | undefined): string {
  const date = parseBackendDate(value);
  return date ? `${date.year}-${date.month}-${date.day}` : '';
}
